import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tabs, Spin, Avatar, Button, Popconfirm, Modal, Input } from 'antd';
import { TeamOutlined, MessageOutlined, BellOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import notificationService from '../../services/notificationService';
import useStyles from '../../styles/components/notification/NotificationModal';
import EmptyState from '../common/EmptyState';
import { formatDate } from '../../utils/dateUtils';
import { updateUserTeams, setCurrentWorkspace, getUserTeams } from '../../utils/workspaceHelper';
import useAppMessage from '../../hooks/useAppMessage';
import { useRightPanel } from '../../contexts/RightPanelContext';

const TEAM_NOTIFICATION_TYPES = [
  'group_invitation',
  'group_application',
  'invitation_response',
  'application_result',
  'member_removed',
];

const COMMUNITY_NOTIFICATION_TYPES = [
  'comment_received',
  'comment_reply',
  'like_received',
  'follow_received',
  'content_mention',
];

const SYSTEM_NOTIFICATION_TYPES = [
  'certificate_issued',
  'ai_agent_granted',
  'ai_agent_expired',
  'system_message',
];

const NotificationList = () => {
  const { styles } = useStyles();
  const navigate = useNavigate();
  const { message, contextHolder } = useAppMessage();
  const { togglePanel, closePanel, mode } = useRightPanel();
  const scrollRef = useRef(null);
  const popconfirmRef = useRef(null);
  const rejectModalRef = useRef(null);
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
    has_more: true
  });
  const [deletingId, setDeletingId] = useState(null);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingNotification, setRejectingNotification] = useState(null);
  const [rejecting, setRejecting] = useState(false);

  // 获取通知列表
  const fetchNotifications = async (type = 'all', page = 1, append = false) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = {
        page,
        per_page: pagination.per_page
      };
      
      if (type !== 'all') {
        params.category = type;
      }
      
      const response = await notificationService.getNotifications(params);
      const newNotifications = response.notifications || [];
      if (append) {
        setNotifications(prev => [...prev, ...newNotifications]);
      } else {
        setNotifications(newNotifications);
      }
      setUnreadCount(response.unread_count || 0);
      
      // 更新分页信息
      setPagination(prev => ({
        ...prev,
        page,
        total: response.pagination?.total || 0,
        total_pages: response.pagination?.total_pages || 0,
        has_more: page < (response.pagination?.total_pages || 0)
      }));
    } catch (error) {
      window.message?.error('获取通知列表失败');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // 加载下一页
  const loadMore = useCallback(async () => {
    if (loadingMore || !pagination.has_more) return;
    
    const nextPage = pagination.page + 1;
    await fetchNotifications(activeTab, nextPage, true);
  }, [loadingMore, pagination.has_more, pagination.page, activeTab]);

  // 滚动加载处理
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const threshold = 100;
    
    if (scrollHeight - scrollTop - clientHeight < threshold && !loadingMore && pagination.has_more) {
      loadMore();
    }
  }, [loadMore, loadingMore, pagination.has_more]);

  // 标记通知为已读
  const markNotificationAsRead = async (notificationId) => {
    if (!notificationId) return;
    
    try {
      await notificationService.markAsRead(notificationId);
      
      setNotifications(prev => 
        prev.map(item => 
          item.id === notificationId ? { ...item, is_read: true } : item
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (error) {
      window.message?.error('标记已读失败');
    }
  };

  // 批量标记已读
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead({ mark_all: true });
      setNotifications(prev => prev.map(item => ({ ...item, is_read: true })));
      setUnreadCount(0);
      message.success('已全部标记为已读');
    } catch (error) {
      message.error('批量标记已读失败');
    }
  };

  // 处理团队邀请/申请等逻辑与原Modal一致，这里省略部分重复代码以保持简洁，
  // 实际项目中应提取 handleInvitation, handleApplication 等为通用 hook 或 service 方法。
  // 为确保功能完整，这里保留关键处理逻辑。

  const handleInvitation = async (invitationId, action, notificationId, reason = '') => {
    try {
      await markNotificationAsRead(notificationId);
      const response = await notificationService.handleInvitation(invitationId, { action, reason });
      message.success(action === 'accept' ? '已接受邀请' : '已拒绝邀请');

      if (action === 'accept') {
        const currentNotification = notifications.find((n) => n.id === notificationId);
        const teamData = currentNotification?.data;
        if (!teamData || !teamData.group_id) {
          window.message?.error('团队信息不完整，无法跳转');
          return;
        }

        const newTeam = {
          id: teamData.group_id,
          name: teamData.team_name || '未命名团队',
          role: response.team_member?.role || 'member',
          status: 'active',
          joined_at: response.team_member?.joined_at || new Date().toISOString(),
        };

        const currentTeams = getUserTeams();
        const updatedTeams = [...currentTeams, newTeam];
        updateUserTeams(updatedTeams);

        setCurrentWorkspace({
          id: teamData.group_id,
          name: teamData.team_name || '未命名团队',
        });

        navigate(`/workspace/${teamData.group_id}`);
      }
      fetchNotifications(activeTab, 1, false);
    } catch (error) {
      message.error('处理邀请失败');
    }
  };

  const handleApplication = async (teamId, applicationId, action, notificationId, reason = '') => {
    try {
      await markNotificationAsRead(notificationId);
      const actionData = {
        action: action === 'accept' ? 'approve' : 'reject',
        role: action === 'accept' ? 'member' : undefined,
        message: action === 'accept' ? '欢迎加入团队！' : '很抱歉，您的申请未通过。',
        reason,
      };
      const response = await notificationService.handleApplication(teamId, applicationId, actionData);
      message.success(action === 'accept' ? '已批准申请' : '已拒绝申请');
      
      if (action === 'accept' && response.team_member) {
        const currentNotification = notifications.find((n) => n.id === notificationId);
        const teamData = currentNotification?.data;

        if (teamData?.group_id) {
          const newTeam = {
            id: teamData.group_id,
            name: teamData.team_name,
            role: response.team_member.role,
            status: 'active',
            joined_at: response.team_member.joined_at,
          };

          const currentTeams = getUserTeams();
          const updatedTeams = [...currentTeams, newTeam];
          updateUserTeams(updatedTeams);

          setCurrentWorkspace({
            id: teamData.group_id,
            name: teamData.team_name,
          });
        } else {
          window.message?.error('团队信息不完整，无法更新');
        }
      }

      fetchNotifications(activeTab, 1, false);
    } catch (error) {
      message.error('处理申请失败');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      setDeletingId(notificationId);
      await notificationService.deleteNotification(notificationId);
      message.success('通知已删除');
      setNotifications(prev => prev.filter(item => item.id !== notificationId));
      
      setUnreadCount(prev => {
        const deletedNotification = notifications.find(n => n.id === notificationId);
        return deletedNotification && !deletedNotification.is_read ? Math.max(0, prev - 1) : prev;
      });
      
      setPagination(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1)
      }));
    } catch (error) {
      message.error('删除通知失败');
    } finally {
      setDeletingId(null);
    }
  };

  const handleUserClick = async (userId, notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      navigate(`/personal-info/${userId}`);
    } catch (error) {
      navigate(`/personal-info/${userId}`);
    }
  };

  const showRejectModal = (notification) => {
    setRejectingNotification(notification);
    setRejectReason('');
    setRejectModalVisible(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectingNotification) return;
    
    setRejecting(true);
    try {
      const { type, data, id } = rejectingNotification;
      
      if (type === 'group_invitation') {
        await handleInvitation(data.invitation_id, 'reject', id, rejectReason);
      } else if (type === 'group_application') {
        await handleApplication(data.group_id, data.application_id, 'reject', id, rejectReason);
      }
      
      setRejectModalVisible(false);
      setRejectReason('');
      setRejectingNotification(null);
    } catch (error) {
      message?.error?.('拒绝操作失败，请稍后重试');
    } finally {
      setRejecting(false);
    }
  };

  const handleCancelReject = () => {
    setRejectModalVisible(false);
    setRejectReason('');
    setRejectingNotification(null);
  };

  const getTypeText = (type) => {
    if (TEAM_NOTIFICATION_TYPES.includes(type)) return '团队相关';
    if (COMMUNITY_NOTIFICATION_TYPES.includes(type)) return '社区互动';
    if (SYSTEM_NOTIFICATION_TYPES.includes(type)) return '系统通知';
    return '其他';
  };

  const getTypeIcon = (type) => {
    if (TEAM_NOTIFICATION_TYPES.includes(type)) return <TeamOutlined />;
    if (COMMUNITY_NOTIFICATION_TYPES.includes(type)) return <MessageOutlined />;
    return <BellOutlined />;
  };

  const generateMessageContent = (type, data, content) => {
    const teamName = data?.team_name || data?.group_name || '团队';

    switch (type) {
      case 'group_invitation':
        return `${data?.inviter_name || '成员'}邀请您加入团队${teamName}`;
      case 'group_application':
        return `${data?.applicant_name || '申请人'}申请加入团队${teamName}`;
      case 'invitation_response': {
        const inviteeName = data?.invitee_name || '成员';
        let actionText = '回复';
        if (data?.action === 'accept') {
          actionText = '接受';
        } else if (data?.action === 'reject') {
          actionText = '拒绝';
        }
        return `${inviteeName}${actionText}了您的团队邀请`;
      }
      case 'application_result': {
        if (data?.action === 'approve') {
          return `您的申请已通过，成功加入团队${teamName}`;
        }
        if (data?.action === 'reject') {
          return `您的申请未通过，未能加入团队${teamName}`;
        }
        return `您申请加入团队${teamName}的状态有更新`;
      }
      case 'member_removed': {
        if (data?.action === 'removed_from_group') {
          return `您已被移出团队${teamName}`;
        }
        const remover = data?.removed_by_name || '管理员';
        const removedUser = data?.removed_user_name || '成员';
        return `${remover}将${removedUser}从团队${teamName}中移除`;
      }
      case 'comment_received': {
        const targetTitle = data?.target_title || data?.proposal_title || data?.content_title;
        return `${data?.commenter_name || '用户'}评论了您的内容${targetTitle ? `「${targetTitle}」` : ''}`;
      }
      case 'comment_reply': {
        const targetTitle = data?.target_title || data?.proposal_title || data?.content_title;
        return `${data?.replier_name || '用户'}回复了${targetTitle ? `您在「${targetTitle}」的评论` : '您的评论'}`;
      }
      case 'like_received': {
        const targetTitle = data?.target_title || data?.proposal_title || data?.content_title;
        const targetType = data?.target_type;
        const noun = targetType === 'comment' ? '评论' : targetType === 'article' ? '文章' : '内容';
        const titleSegment = noun !== '评论' && targetTitle ? `「${targetTitle}」` : '';
        return `${data?.liker_name || '用户'}点赞了您的${noun}${titleSegment}`;
      }
      case 'follow_received':
        return `${data?.follower_name || '用户'}关注了您`;
      case 'content_mention': {
        const targetTitle = data?.target_title || data?.proposal_title || data?.content_title;
        return `${data?.mentioner_name || '用户'}在${targetTitle ? `「${targetTitle}」` : '内容'}中提到了您`;
      }
      default:
        return content || data?.content || '通知消息';
    }
  };

  const getActorInfo = (type, data) => {
    if (type === 'group_invitation') {
      return {
        id: data?.inviter_id,
        name: data?.inviter_name,
        avatar: data?.inviter_avatar,
        fallbackName: '成员',
      };
    }

    if (type === 'group_application') {
      return {
        id: data?.applicant_id,
        name: data?.applicant_name,
        avatar: data?.applicant_avatar,
        fallbackName: '申请人',
      };
    }

    if (type === 'invitation_response') {
      return {
        id: data?.invitee_id,
        name: data?.invitee_name,
        avatar: data?.invitee_avatar,
        fallbackName: '成员',
      };
    }

    if (type === 'application_result') {
      return {
        id: data?.reviewer_id,
        name: data?.reviewer_name,
        avatar: data?.reviewer_avatar,
        fallbackName: '系统',
      };
    }

    if (type === 'member_removed') {
      return {
        id: data?.removed_by_user_id,
        name: data?.removed_by_name,
        avatar: data?.removed_by_avatar,
        fallbackName: '管理员',
      };
    }

    if (type === 'comment_received') {
      return {
        id: data?.commenter_id,
        name: data?.commenter_name,
        avatar: data?.commenter_avatar,
        fallbackName: '用户',
      };
    }

    if (type === 'comment_reply') {
      return {
        id: data?.replier_id,
        name: data?.replier_name,
        avatar: data?.replier_avatar,
        fallbackName: '用户',
      };
    }

    if (type === 'like_received') {
      return {
        id: data?.liker_id,
        name: data?.liker_name,
        avatar: data?.liker_avatar,
        fallbackName: '用户',
      };
    }

    if (type === 'follow_received') {
      return {
        id: data?.follower_id,
        name: data?.follower_name,
        avatar: data?.follower_avatar,
        fallbackName: '用户',
      };
    }

    if (type === 'content_mention') {
      return {
        id: data?.mentioner_id,
        name: data?.mentioner_name,
        avatar: data?.mentioner_avatar,
        fallbackName: '用户',
      };
    }

    if (SYSTEM_NOTIFICATION_TYPES.includes(type)) {
      return {
        fallbackName: '系统',
      };
    }

    return {
      fallbackName: TEAM_NOTIFICATION_TYPES.includes(type) ? '成员' : '用户',
    };
  };

  const handleContentNavigate = async (notification) => {
    if (!notification) return;
    const { id, type, data = {} } = notification;
    try {
      await markNotificationAsRead(id);
      const teamId = data?.group_id;
      if (teamId) {
        navigate(`/workspace/${teamId}`);
        return;
      }
      const category = data?.category;
      const buildContentPath = (contentId, typeHint) => {
        if (category === 'competition') return `/explore/proposals/${contentId}`;
        if (category === 'post') return `/explore/friends/${contentId}`;
        return `/explore/proposals/${contentId}`;
      };

      if (type === 'comment_received' || type === 'comment_reply' || type === 'content_mention') {
        const parentId = data?.parent_id || data?.content_id;
        const parentType = data?.parent_type;
        if (parentId) {
          if (parentType === 'user_profile') {
            navigate(`/personal-info/${parentId}`);
            return;
          }
          if (parentType === 'group_profile') {
            navigate(`/workspace/${parentId}`);
            return;
          }
          navigate(buildContentPath(parentId, parentType));
          return;
        }
      }
      if (type === 'like_received') {
        const parentId = data?.parent_id || data?.content_id;
        const parentType = data?.parent_type || data?.target_type;
        if (parentId) {
          if (parentType === 'user_profile') {
            navigate(`/personal-info/${parentId}`);
            return;
          }
          if (parentType === 'group_profile') {
            navigate(`/workspace/${parentId}`);
            return;
          }
          navigate(buildContentPath(parentId, parentType));
          return;
        }
      }
    } catch (error) {
      const teamId = data?.group_id;
      if (teamId) {
        navigate(`/workspace/${teamId}`);
        return;
      }
    }
  };

  // Initial fetch
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1, has_more: true }));
    fetchNotifications(activeTab, 1, false);
  }, []);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPagination(prev => ({ ...prev, page: 1, has_more: true }));
    fetchNotifications(key, 1, false);
  };

  const handleClose = () => {
    if (mode === 'drawer') {
      closePanel();
      return;
    }
    togglePanel('hot');
  };

  const renderNotificationItem = (notification) => {
    const { id, type, content, data, is_read, inserted_at } = notification;
    const actorInfo = getActorInfo(type, data || {});
    const actorId = actorInfo.id;
    const actorName = actorInfo.name || actorInfo.fallbackName || '系统';
    const actorAvatar = actorInfo.avatar;
    const actorInitial = actorName?.[0] || '系';
    const isActorClickable = Boolean(actorId);

    return (
      <div key={id} className={styles.notificationItem}>
        <div className={styles.notificationBackground}>
          <div className={styles.notificationHeader}>
            <div className={styles.headerLeft}>
              <div className={styles.typeIcon}>
                {getTypeIcon(type)}
              </div>
              <span className={styles.typeText}>
                {getTypeText(type)}
              </span>
              {!is_read && <div className={styles.unreadDot} data-testid="unread-dot" />}
            </div>
            <div className={styles.headerRight}>
              <span className={styles.time}>
                {formatDate(new Date(inserted_at))}
              </span>
              <Popconfirm
                title="删除通知"
                description="确定要删除这条通知吗？删除后无法恢复。"
                onConfirm={(e) => {
                  e?.stopPropagation?.();
                  handleDelete(id);
                }}
                okText="确定"
                cancelText="取消"
                okButtonProps={{ 
                  danger: true,
                  loading: deletingId === id 
                }}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              >
                <DeleteOutlined 
                  className={styles.moreButton} 
                  style={{ 
                    opacity: deletingId === id ? 0.5 : 1,
                    cursor: deletingId === id ? 'not-allowed' : 'pointer'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
              </Popconfirm>
            </div>
          </div>
          
          <div className={styles.notificationContent}>
            <div 
              className={styles.userAvatar}
              onClick={() => {
                if (actorId) {
                  handleUserClick(actorId, id);
                }
              }}
              style={{ cursor: isActorClickable ? 'pointer' : 'default' }}
            >
              <Avatar
                data-testid="notification-avatar"
                src={actorAvatar}
                size={46}
              >
                {actorInitial}
              </Avatar>
            </div>
            
            <div className={styles.contentArea}>
              <div 
                className={styles.userName}
                onClick={() => {
                  if (actorId) {
                    handleUserClick(actorId, id);
                  }
                }}
              >
                {actorName}
              </div>
              
              <div className={`${styles.messageContent}`} onClick={() => handleContentNavigate(notification)}>
                {generateMessageContent(type, data, content)}
              </div>
            </div>
            
            {(type === 'group_invitation' || type === 'group_application') && !notification.is_processed && (
              <div className={styles.actionButtons}>
                <Button
                  className={`${styles.actionButton} ${styles.agreeButton}`}
                  onClick={() => {
                    if (type === 'group_invitation') {
                      handleInvitation(data.invitation_id, 'accept', id);
                    } else if (type === 'group_application') {
                      handleApplication(data.group_id, data.application_id, 'accept', id);
                    }
                  }}
                >
                  {type === 'group_invitation' ? '同意' : '批准'}
                </Button>
                <Button
                  className={`${styles.actionButton} ${styles.rejectButton}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    showRejectModal(notification);
                  }}
                >
                  {type === 'group_invitation' ? '拒绝' : '拒绝'}
                </Button>
              </div>
            )}
  
            {(type === 'group_invitation' || type === 'group_application') && notification.is_processed && (
              <div className={styles.processedStatus}>
                <span className={styles.processedText}>已处理</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {contextHolder}
      <div className={styles.notificationPanel}>
        <div className={styles.modalHeader} style={{ padding: '16px 16px 0' }}>
          <h3 className={styles.title} style={{ fontSize: 20, paddingLeft: 48, minHeight: 40, lineHeight: '40px' }}>通知消息</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button 
              className={styles.markAllReadButton}
              onClick={handleMarkAllAsRead}
            >
              全部已读
            </button>
          </div>
        </div>

        <div className={styles.categoryTabs} style={{ padding: '8px 12px' }}>
          <Tabs 
            activeKey={activeTab} 
            onChange={handleTabChange}
            size="small"
            items={[
              { key: 'all', label: '全部' },
              { key: 'team', label: '团队相关' },
              { key: 'community', label: '社区互动' },
              { key: 'system', label: '系统通知' },
            ]}
          />
        </div>

        <div 
          ref={scrollRef}
          className={styles.notificationList}
          style={{ padding: '0 16px 16px' }}
          onScroll={handleScroll}
          data-testid="notification-list"
        >
          {loading ? (
            <div className={styles.loadingState}>
              <Spin size="large" />
            </div>
          ) : notifications.length > 0 ? (
            <>
              {notifications.map(renderNotificationItem)}
              {pagination.has_more && (
                <div className={styles.loadMoreContainer}>
                  {loadingMore ? (
                    <div className={styles.loadMoreLoading}>
                      <Spin size="small" />
                      <span>加载中...</span>
                    </div>
                  ) : (
                    <div className={styles.loadMoreText}>
                      上拉加载更多
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <EmptyState />
            </div>
          )}
        </div>

        <div
          className="flex items-center justify-center"
          style={{ height: '132px', flexShrink: 0 }}
        >
          <button 
            aria-label="关闭多功能区"
            onClick={handleClose}
            className="w-[92px] h-[92px] rounded-full flex items-center justify-center cursor-pointer hover:bg-white/90 transition-colors"
            style={{
              background: '#F8F8F8',
              padding: '34px',
              gap: '10px'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="#00000E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 6L18 18" stroke="#00000E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <div ref={rejectModalRef}>
        <Modal
          title="拒绝理由"
          open={rejectModalVisible}
          onOk={handleConfirmReject}
          onCancel={handleCancelReject}
          okText="确认拒绝"
          cancelText="取消"
          okButtonProps={{ 
            danger: true,
            loading: rejecting 
          }}
          confirmLoading={rejecting}
        >
          <div style={{ marginBottom: '16px' }}>
            <p style={{ marginBottom: '8px', color: '#666' }}>
              请输入拒绝理由（可选）：
            </p>
            <Input.TextArea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="请输入拒绝理由..."
              rows={4}
              maxLength={200}
              showCount
            />
          </div>
        </Modal>
      </div>
    </>
  );
};

export default NotificationList;
